import { useState } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import PortalBottomNav from "../components/PortalBottomNav";
import PortalHeader from "../components/PortalHeader";
import {
  ArrowLeft,
  Car,
  Heart,
  Users,
  Home,
  Coffee,
  Utensils,
  Shirt,
  Sparkles,
  Calendar,
  ShoppingCart,
  UserPlus,
  Plane,
  CheckSquare,
  Camera,
  ChevronDown,
  ChevronUp,
  Phone,
  MessageCircle,
  Zap,
  Volume2,
  Cigarette,
  Music,
  PawPrint,
  Droplets,
  Tag,
  Trash2,
  Sun,
  Moon,
  Flame,
  Wind,
  Laptop,
  Key,
  Check,
  Bell,
  User,
  MapPin
} from "lucide-react";

// Handbook sections data
const HANDBOOK_SECTIONS = [
  {
    id: "welcome",
    title: "Welcome to Villa Savi",
    icon: Heart,
    color: "bg-[#C85A38]",
    content: {
      intro: "A home for intentional living, creative flow, and regenerative community.",
      values: [
        { title: "Intentionality", desc: "Live with purpose and presence" },
        { title: "Respect", desc: "For yourself, others, and the space" },
        { title: "Growth", desc: "Learn, reflect, evolve" },
        { title: "Contribution", desc: "Give back to the community" },
        { title: "Balance", desc: "Between work, rest, solitude, and play" }
      ],
      quote: "We live well to think clearly. We think clearly to live better."
    }
  },
  {
    id: "team",
    title: "Your Savi Team",
    icon: Users,
    color: "bg-[#8C9A7C]",
    content: {
      members: [
        {
          name: "Steve",
          role: "Landlord & Operations",
          phone: "+34 632 628 051",
          desc: "Handles rooms, schedules, check-in/out, and house standards. Keeps things running smoothly.",
          avatar: "S"
        },
        {
          name: "Marina",
          role: "Community & Guest Experience",
          phone: "+34 650 208 927",
          desc: "Helps you settle in, understand the Savi rhythm, and find your flow. Go-to for community vibes and shopping.",
          avatar: "M"
        }
      ],
      volunteers: "They support the daily flow, helping with cleaning, laundry, garden tasks, meal prep, and content."
    }
  },
  {
    id: "rules",
    title: "House Guidelines",
    icon: Home,
    color: "bg-[#1A1A1A]",
    content: {
      rules: [
        { icon: Moon, title: "Quiet Hours", desc: "10 PM to 8 AM - Keep things soft for rest and focus" },
        { icon: Home, title: "Shoes Off", desc: "Help us keep it clean and comfy for all" },
        { icon: Cigarette, title: "Smoke-Free", desc: "Use designated outdoor area only" },
        { icon: Music, title: "Low Music", desc: "Respect neighbors and keep good vibes flowing quietly" },
        { icon: Coffee, title: "No Alcohol/Drugs", desc: "A clear and shared commitment" },
        { icon: PawPrint, title: "No Animals in the Main House or Kitchen", desc: "Everywhere else on site is fine, and the whole property is fully fenced" },
        { icon: Flame, title: "No Barbecues", desc: "Until further notice — keeping us safe from fire hazards" },
        { icon: Wind, title: "Salon Windows", desc: "Please don't open the windows by the speakers in the salon" },
        { icon: Droplets, title: "Mind Resources", desc: "Be thoughtful with water and electricity" },
        { icon: UserPlus, title: "Guests by Approval", desc: "Request overnight guests in advance" },
        { icon: Tag, title: "Label What's Yours", desc: "Unlabeled food means shared" },
        { icon: Sparkles, title: "Keep It Clear", desc: "Shared spaces tidy and clutter-free" },
        { icon: Trash2, title: "Use It, Clean It", desc: "Return shared items clean and ready" }
      ]
    }
  },
  {
    id: "kitchen",
    title: "Kitchen Rules",
    icon: Coffee,
    color: "bg-[#F2CC8F]",
    content: {
      intro: "This is the heart of our home. Let's keep it bright, clean, and welcoming.",
      rules: [
        { step: "1", title: "Rinse → Load", desc: "Quick rinse, straight into dishwasher" },
        { step: "2", title: "Empty if Clean", desc: "Spot a clean machine? Please empty it" },
        { step: "3", title: "Last One In?", desc: "Hit 'Start' - 1-hour cycle only" },
        { step: "4", title: "Pots & Pans", desc: "Hand-wash, towel-dry, return to hooks" },
        { step: "5", title: "Wipe Surfaces", desc: "Disinfect counters after cooking" },
        { step: "6", title: "Label Food", desc: "Untagged = community fuel" },
        { step: "7", title: "Recycling", desc: "Plastic • Paper • Glass • Organic" }
      ]
    }
  },
  {
    id: "laundry",
    title: "Laundry & Cleaning",
    icon: Shirt,
    color: "bg-[#8C9A7C]",
    content: {
      laundry: {
        intro: "Self-service, easy, and designed to fit around your rhythm.",
        sorting: ["Whites", "Colors", "Bedding & towels"],
        tip: "Remove laundry promptly so others can use the space.",
        service: "Prefer it done for you? Full wash & dry service for €50/month — speak to Marina."
      },
      cleaning: {
        schedule: "Common areas cleaned Monday–Friday",
        rooms: "Room cleaning happens weekly, on the same day as your check-in",
        note: "Make sure room is tidy and unlocked that morning"
      }
    }
  },
  {
    id: "living",
    title: "Savi Living",
    icon: Sparkles,
    color: "bg-[#C85A38]",
    content: {
      intro: "This space is more than where you sleep — it's a living system designed to support your wellbeing, creativity, and connection.",
      offerings: [
        "Community meals",
        "Movement & wellness classes",
        "Group check-ins or gatherings",
        "Massage, facials, and treatments",
        "Workshops, talks, creative sessions",
        "Bike rentals and outdoor activities"
      ],
      cta: "Check in with Marina to see what's happening or get involved!"
    }
  },
  {
    id: "meals",
    title: "Grocery & Meals",
    icon: Utensils,
    color: "bg-[#F2CC8F]",
    content: {
      grocery: {
        intro: "Most guests shop for themselves — Náquera is a five-minute drive and has everything you need. There's no house shopping shuttle; housemates often offer lifts via the Rides tab, but that's goodwill, not a schedule.",
        delivery: "Consum and Mercadona both deliver to the house (a little Spanish helps)"
      },
      meals: {
        intro: "Fresh, nourishing meals without the prep, shopping, or fuss. Offered as a weekly breakfast + lunch package — not individual drop-ins.",
        package: { price: "€30 / day", includes: "Breakfast + lunch", minimum: "Minimum 1 full week", deadline: "Sign-up must be confirmed by Sunday" },
        times: [
          { meal: "Breakfast", time: "9:00 – 9:30" },
          { meal: "Lunch", time: "14:00 – 15:00" }
        ],
        sample: "Expect proper, feel-good food: fresh breakfasts, healthy bowls, pastas, salads, burgers, tacos, fish, seasonal sides and simple desserts.",
        sunday: "Sunday Paella or Fideuá — €15.50"
      }
    }
  },
  {
    id: "coworking",
    title: "The Coworking",
    icon: Laptop,
    color: "bg-[#3D84A8]",
    content: {
      intro: "The coworking space is mainly for focused work.",
      rules: [
        "Keep noise low and use headphones where possible — others may be doing deep work",
        "Quick calls are fine; longer, repeated or loud calls should move to your room, the terrace or outside",
        "Not a silent library, but not a call centre — everyone should be able to work properly",
        "Need calls all day? Use the Zoom Room downstairs (first-come, first-served)"
      ]
    }
  },
  {
    id: "electricity",
    title: "Electricity & Solar Power",
    icon: Zap,
    color: "bg-[#F2CC8F]",
    content: {
      intro: "Savi runs on off-grid, solar-powered energy — a beautiful and limited resource. Energy usage is monitored through our home automation system.",
      note: "Excessive or inappropriate use (heaters, AC units or other high-draw appliances run unnecessarily) may result in an additional charge.",
      tip: "If you're ever unsure, just ask — we're here to help you live well off-grid."
    }
  },
  {
    id: "transport",
    title: "Getting Around",
    icon: Car,
    color: "bg-[#8C9A7C]",
    content: {
      intro: "Savi sits in the hills, five minutes' drive from Náquera, so life here is a lot easier with your own wheels — a car or a scooter.",
      points: [
        "We don't rent cars, but we do have bikes you can borrow or rent",
        "Náquera has supermarkets, a pharmacy, cafés and the bus to Valencia",
        "There's no shopping shuttle — Savi doesn't run trips to town",
        "Housemates often head to Náquera and offer lifts in the Rides tab — lovely when it happens, never guaranteed"
      ],
      note: "Going without a vehicle? Set up food delivery to the house — Consum and Mercadona both deliver. Please don't rely on lifts for your weekly shop."
    }
  },
  {
    id: "gatekey",
    title: "Gate Remote & Key Deposit",
    icon: Key,
    color: "bg-[#8C9A7C]",
    content: {
      intro: "There's a €100 cash deposit due on arrival for the gate remote that gives car access to the house. No car? No remote needed.",
      note: "Refunded when you return the remote in working condition at check-out. This is separate from your rent payment."
    }
  },
  {
    id: "guests",
    title: "Guest Policy",
    icon: UserPlus,
    color: "bg-[#1A1A1A]",
    content: {
      limits: [
        "Max 3 nights per visit, up to 6 nights/month",
        "Max 2 guest visits/month"
      ],
      approval: "Message Property Manager 24h in advance with guest name, dates, room number",
      fees: "€25/night, paid in cash",
      rules: [
        "No sharing keys or door codes",
        "All guests must sign in and show ID if asked",
        "No unregistered or surprise guests",
        "No guests during quiet hours without approval"
      ]
    }
  },
  {
    id: "arrival",
    title: "Arrival & Departure",
    icon: Plane,
    color: "bg-[#8C9A7C]",
    content: {
      before: [
        "Fill out the Savi Resident Intake form",
        "Join the Savi People WhatsApp group",
        "Read through this guide thoroughly"
      ],
      pickup: "Airport transfers available - message Steve 48h in advance",
      onArrival: [
        "Welcome tour + room walkthrough",
        "Get the lay of the land (and the village)"
      ],
      beforeLeaving: [
        "Share your feedback — voice note, scribble, or selfie video",
        "Leave a note in the guestbook — a little imprint of your time"
      ]
    }
  },
  {
    id: "checkout",
    title: "Check-Out Checklist",
    icon: CheckSquare,
    color: "bg-[#C85A38]",
    content: {
      intro: "Before you leave, reset your space for the next guest:",
      items: [
        "Remove any rubbish from your room",
        "Do a quick tidy if needed",
        "Return your key and gate remote (deposit refunded)",
        "Check drawers and shelves — nothing left behind",
        "Leave windows closed and lights off"
      ],
      thanks: "Thanks for leaving the space just a little brighter than you found it!"
    }
  },
  {
    id: "community",
    title: "Community & Content",
    icon: Camera,
    color: "bg-[#F2CC8F]",
    content: {
      social: {
        handle: "@savicoliving",
        hashtags: ["#colivingspain", "#villalife", "#slowtravel"],
        tip: "Tag us in your posts and stories — we often reshare!"
      },
      content: "Capturing the vibe? Be mindful of others' privacy. Got great photos? Send them our way!",
      closing: "Thank you for being part of Villa Savi — more than a place to stay, this is a shared home and community."
    }
  }
];

const HandbookPage = () => {
  const [expandedSection, setExpandedSection] = useState("welcome");

  const toggleSection = (id) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  const renderSectionContent = (section) => {
    const { content } = section;
    
    switch (section.id) {
      case "welcome":
        return (
          <div className="space-y-6">
            <p className="text-[#1A1A1A]/80 text-lg leading-relaxed">{content.intro}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {content.values.map((value, i) => (
                <div key={i} className="bg-[#F0EDE3] rounded-xl p-4">
                  <p className="font-semibold text-[#1A1A1A]">{value.title}</p>
                  <p className="text-sm text-[#1A1A1A]/60">{value.desc}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#C85A38]/10 rounded-xl p-6 text-center">
              <p className="font-serif text-xl text-[#C85A38] italic">"{content.quote}"</p>
            </div>
          </div>
        );
      
      case "team":
        return (
          <div className="space-y-4">
            {content.members.map((member, i) => (
              <div key={i} className="bg-white rounded-xl p-4 border border-stone-100 flex gap-4">
                <div className="w-14 h-14 rounded-full bg-[#8C9A7C] flex items-center justify-center text-white font-serif text-xl flex-shrink-0">
                  {member.avatar}
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-[#1A1A1A]">{member.name}</p>
                      <p className="text-sm text-[#C85A38]">{member.role}</p>
                    </div>
                    <a href={`tel:${member.phone.replace(/\s/g, '')}`} className="text-[#8C9A7C]">
                      <Phone className="w-5 h-5" />
                    </a>
                  </div>
                  <p className="text-sm text-[#1A1A1A]/60 mt-2">{member.desc}</p>
                  <a href={`https://wa.me/${member.phone.replace(/[^0-9]/g, '')}`} className="inline-flex items-center gap-1 text-xs text-[#25D366] mt-2">
                    <MessageCircle className="w-3 h-3" /> WhatsApp
                  </a>
                </div>
              </div>
            ))}
            <div className="bg-[#F0EDE3] rounded-xl p-4">
              <p className="font-semibold text-[#1A1A1A] mb-1">Team Members</p>
              <p className="text-sm text-[#1A1A1A]/60">{content.volunteers}</p>
            </div>
          </div>
        );
      
      case "rules":
        return (
          <div className="grid gap-2">
            {content.rules.map((rule, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-3 border border-stone-100">
                <div className="w-10 h-10 rounded-full bg-[#F0EDE3] flex items-center justify-center flex-shrink-0">
                  <rule.icon className="w-5 h-5 text-[#1A1A1A]" />
                </div>
                <div>
                  <p className="font-medium text-[#1A1A1A]">{rule.title}</p>
                  <p className="text-sm text-[#1A1A1A]/60">{rule.desc}</p>
                </div>
              </div>
            ))}
          </div>
        );
      
      case "kitchen":
        return (
          <div className="space-y-4">
            <p className="text-[#1A1A1A]/80">{content.intro}</p>
            <div className="space-y-2">
              {content.rules.map((rule, i) => (
                <div key={i} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-stone-100">
                  <div className="w-8 h-8 rounded-full bg-[#F2CC8F] flex items-center justify-center font-semibold text-[#1A1A1A]">
                    {rule.step}
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1A1A]">{rule.title}</p>
                    <p className="text-xs text-[#1A1A1A]/60">{rule.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      
      case "laundry":
        return (
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-[#1A1A1A] mb-2 flex items-center gap-2">
                <Shirt className="w-4 h-4" /> Laundry
              </h4>
              <p className="text-sm text-[#1A1A1A]/70 mb-3">{content.laundry.intro}</p>
              <div className="flex gap-2 flex-wrap">
                {content.laundry.sorting.map((item, i) => (
                  <span key={i} className="bg-[#F0EDE3] px-3 py-1 rounded-full text-sm text-[#1A1A1A]">{item}</span>
                ))}
              </div>
              <p className="text-xs text-[#C85A38] mt-2">{content.laundry.tip}</p>
              <div className="bg-[#F0EDE3] rounded-xl p-3 mt-3">
                <p className="text-sm text-[#1A1A1A]">{content.laundry.service}</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-[#1A1A1A] mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Cleaning
              </h4>
              <div className="space-y-2 text-sm text-[#1A1A1A]/70">
                <p>• {content.cleaning.schedule}</p>
                <p>• {content.cleaning.rooms}</p>
                <p className="text-xs text-[#C85A38]">{content.cleaning.note}</p>
              </div>
            </div>
          </div>
        );
      
      case "living":
        return (
          <div className="space-y-4">
            <p className="text-[#1A1A1A]/80">{content.intro}</p>
            <div className="grid grid-cols-2 gap-2">
              {content.offerings.map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-3 border border-stone-100 text-sm text-[#1A1A1A]">
                  <Sun className="w-4 h-4 text-[#C85A38] mb-1" />
                  {item}
                </div>
              ))}
            </div>
            <div className="bg-[#C85A38]/10 rounded-xl p-4 text-center">
              <p className="text-sm text-[#C85A38]">{content.cta}</p>
            </div>
          </div>
        );
      
      case "meals":
        return (
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-[#1A1A1A] mb-2">Grocery Shopping</h4>
              <p className="text-sm text-[#1A1A1A]/70">{content.grocery.intro}</p>
              <p className="text-xs text-[#1A1A1A]/50 mt-1">{content.grocery.delivery}</p>
            </div>
            <div>
              <h4 className="font-semibold text-[#1A1A1A] mb-2">Meal Services & Shared Tables</h4>
              <p className="text-sm text-[#1A1A1A]/70 mb-3">{content.meals.intro}</p>
              <div className="bg-white rounded-xl p-4 border border-stone-100 text-center mb-3">
                <p className="font-serif text-2xl text-[#C85A38]">{content.meals.package.price}</p>
                <p className="text-sm text-[#1A1A1A]/70">{content.meals.package.includes}</p>
                <p className="text-xs text-[#1A1A1A]/50 mt-1">{content.meals.package.minimum} · {content.meals.package.deadline}</p>
              </div>
              <div className="flex gap-3 justify-center mb-3">
                {content.meals.times.map((item, i) => (
                  <div key={i} className="bg-[#F0EDE3] rounded-xl p-3 text-center min-w-[110px]">
                    <p className="text-xs text-[#1A1A1A]/60">{item.meal}</p>
                    <p className="font-semibold text-[#1A1A1A]">{item.time}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#1A1A1A]/60">{content.meals.sample}</p>
              <p className="text-sm text-[#8C9A7C] text-center mt-3">🥘 {content.meals.sunday}</p>
            </div>
          </div>
        );
      
      case "guests":
        return (
          <div className="space-y-4">
            <div className="bg-[#F0EDE3] rounded-xl p-4">
              <p className="font-semibold text-[#1A1A1A] mb-2">Stay Limits</p>
              {content.limits.map((limit, i) => (
                <p key={i} className="text-sm text-[#1A1A1A]/70">• {limit}</p>
              ))}
            </div>
            <div className="bg-white rounded-xl p-4 border border-stone-100">
              <p className="font-semibold text-[#1A1A1A] mb-1">Approval Required</p>
              <p className="text-sm text-[#1A1A1A]/70">{content.approval}</p>
            </div>
            <div className="bg-[#C85A38]/10 rounded-xl p-4 text-center">
              <p className="font-semibold text-[#C85A38]">{content.fees}</p>
            </div>
            <div className="space-y-1">
              {content.rules.map((rule, i) => (
                <p key={i} className="text-sm text-[#1A1A1A]/70">• {rule}</p>
              ))}
            </div>
          </div>
        );
      
      case "arrival":
        return (
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-[#1A1A1A] mb-2">Before You Arrive</h4>
              {content.before.map((item, i) => (
                <p key={i} className="text-sm text-[#1A1A1A]/70 flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#8C9A7C]" /> {item}
                </p>
              ))}
            </div>
            <div className="bg-[#F0EDE3] rounded-xl p-4">
              <p className="font-semibold text-[#1A1A1A] mb-1">Airport Pickup</p>
              <p className="text-sm text-[#1A1A1A]/70">{content.pickup}</p>
            </div>
            <div>
              <h4 className="font-semibold text-[#1A1A1A] mb-2">When You Arrive</h4>
              {content.onArrival.map((item, i) => (
                <p key={i} className="text-sm text-[#1A1A1A]/70">• {item}</p>
              ))}
            </div>
            <div>
              <h4 className="font-semibold text-[#1A1A1A] mb-2">Before You Leave</h4>
              {content.beforeLeaving.map((item, i) => (
                <p key={i} className="text-sm text-[#1A1A1A]/70">• {item}</p>
              ))}
            </div>
          </div>
        );
      
      case "checkout":
        return (
          <div className="space-y-4">
            <p className="text-[#1A1A1A]/80">{content.intro}</p>
            <div className="space-y-2">
              {content.items.map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-stone-100">
                  <div className="w-6 h-6 rounded-full bg-[#8C9A7C]/20 flex items-center justify-center">
                    <Check className="w-4 h-4 text-[#8C9A7C]" />
                  </div>
                  <p className="text-sm text-[#1A1A1A]">{item}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#C85A38]/10 rounded-xl p-4 text-center">
              <p className="text-sm text-[#C85A38]">{content.thanks}</p>
            </div>
          </div>
        );
      
      case "community":
        return (
          <div className="space-y-4">
            <div className="bg-gradient-to-br from-[#C85A38] to-[#F2CC8F] rounded-xl p-4 text-white">
              <p className="font-semibold text-lg">@{content.social.handle.replace('@', '')}</p>
              <div className="flex gap-2 mt-2 flex-wrap">
                {content.social.hashtags.map((tag, i) => (
                  <span key={i} className="bg-white/20 px-2 py-1 rounded-full text-xs">{tag}</span>
                ))}
              </div>
              <p className="text-sm mt-3 text-white/90">{content.social.tip}</p>
            </div>
            <p className="text-sm text-[#1A1A1A]/70">{content.content}</p>
            <div className="bg-[#F0EDE3] rounded-xl p-4 text-center">
              <Heart className="w-6 h-6 text-[#C85A38] mx-auto mb-2" />
              <p className="text-sm text-[#1A1A1A]">{content.closing}</p>
            </div>
          </div>
        );
      
      case "coworking":
        return (
          <div className="space-y-3">
            <p className="text-[#1A1A1A]/80">{content.intro}</p>
            {content.rules.map((rule, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-3 border border-stone-100">
                <Laptop className="w-4 h-4 text-[#3D84A8] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-[#1A1A1A]/70">{rule}</p>
              </div>
            ))}
          </div>
        );

      case "electricity":
        return (
          <div className="space-y-4">
            <p className="text-[#1A1A1A]/80">{content.intro}</p>
            <div className="bg-[#C85A38]/10 rounded-xl p-4">
              <p className="text-sm text-[#C85A38]">{content.note}</p>
            </div>
            <p className="text-sm text-[#1A1A1A]/60">{content.tip}</p>
          </div>
        );

      case "transport":
        return (
          <div className="space-y-4" data-testid="handbook-transport-content">
            <p className="text-[#1A1A1A]/80">{content.intro}</p>
            <ul className="space-y-2">
              {content.points.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#1A1A1A]/70">
                  <span className="text-[#8C9A7C] mt-0.5">•</span>
                  {item}
                </li>
              ))}
            </ul>
            <div className="bg-[#F0EDE3] rounded-xl p-4">
              <p className="text-sm text-[#1A1A1A]">{content.note}</p>
            </div>
          </div>
        );

      case "gatekey":
        return (
          <div className="space-y-4">
            <p className="text-[#1A1A1A]/80">{content.intro}</p>
            <div className="bg-[#F0EDE3] rounded-xl p-4">
              <p className="text-sm text-[#1A1A1A]">{content.note}</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF7F1] pb-24 savi-bg-pattern">
      {/* Header */}
      <PortalHeader
        title="Guest Handbook"
        testid="handbook-header"
        right={<span className="text-[11px] text-[#1A1A1A]/40">Villa Savi 2026 · V2.4</span>}
      />

      {/* Welcome */}
      <div className="py-8 px-6 text-center">
        <h2 className="font-serif text-3xl text-[#1A1A1A] mb-2">Welcome to Villa Savi</h2>
        <p className="text-[#1A1A1A]/55 text-sm">Live well. Think clearly. Connect deeply.</p>
      </div>

      {/* Sections */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-3">
          {HANDBOOK_SECTIONS.map((section) => (
            <div
              key={section.id}
              className="bg-white rounded-2xl border border-stone-100 overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-5 py-4 flex items-center gap-4 text-left"
                data-testid={`handbook-section-${section.id}`}
              >
                <div className={`w-10 h-10 rounded-xl ${section.color} flex items-center justify-center`}>
                  <section.icon className="w-5 h-5 text-white" />
                </div>
                <span className="flex-1 font-medium text-[#1A1A1A]">{section.title}</span>
                {expandedSection === section.id ? (
                  <ChevronUp className="w-5 h-5 text-[#1A1A1A]/40" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#1A1A1A]/40" />
                )}
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  expandedSection === section.id ? "max-h-[2000px]" : "max-h-0"
                }`}
              >
                <div className="px-5 pb-5">
                  {renderSectionContent(section)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="max-w-2xl mx-auto px-4 pb-8 text-center space-y-2">
          <p className="text-[#1A1A1A]/40 text-sm">
            One connection, one moment, one day at a time.
          </p>
          <a
            href={`${API}/handbook/download`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#8C9A7C] hover:underline inline-block"
            data-testid="handbook-pdf-link"
          >
            Download the PDF version (V2.4) →
          </a>
        </div>
      </div>

      {/* Bottom Navigation */}
      <PortalBottomNav />
    </div>
  );
};

export default HandbookPage;
